import React, { useState, useEffect } from 'react';
import useResponsive from '../utils/useResponsive';
import { creditoExcedeInmueble, MENSAJE_CREDITO_INMUEBLE } from '../utils/creditoVsInmueble';

// Estado Civil y Situación de Vivienda ya no se capturan aquí: se derivan de lo
// capturado en el módulo Titular, mapeado contra el catálogo propio de Afirme.
const AFIRME_ESTADO_CIVIL_MAP = {
  'Soltero': 'Soltero',
  'Divorciado': 'Divorciado',
  'Viudo': 'Viudo',
  'Union Libre': 'Otro',
  'Sociedad de Convivencia': 'Otro',
};
// Estado Civil del Titular que, en el catálogo de Afirme, caen en "Otro" y
// requieren especificarse a mano en el formato físico.
const AFIRME_ESTADOS_CIVILES_OTRO_ESPECIFICAR = ['Union Libre', 'Sociedad de Convivencia'];
const AFIRME_VIVE_EN_CASA_MAP = {
  'Propia': 'Propia',
  'Hipotecada': 'Propia en Pago',
  'Rentada': 'Renta',
  'Padres': 'Padres',
  'Otro Parentesco': 'Otro Parentesco',
};

function nombreEstadoCivilAfirme(estadoCivil, regimen) {
  if (estadoCivil === 'Casado') {
    if (regimen === 'Sociedad Conyugal') return 'Casado B.Mancomunados';
    if (regimen === 'Separación de Bienes') return 'Casado B. Separados';
    return null;
  }
  return AFIRME_ESTADO_CIVIL_MAP[estadoCivil] || null;
}

function idPorNombre(catalogo, nombre) {
  if (!nombre) return '';
  const match = catalogo.find(c => c.nombre === nombre);
  return match ? match.id : '';
}

const BancoAfirme = ({ prospectoId, formData, setFormData }) => {
  const API = '/api';
  const { isMobile, isTablet } = useResponsive();
  const gridCols = (desktopCols) => (isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : desktopCols);

  // Catálogos
  const [estadosCiviles, setEstadosCiviles] = useState([]);
  const [gradosEstudios, setGradosEstudios] = useState([]);
  const [situacionesVivienda, setSituacionesVivienda] = useState([]);
  const [ocupaciones, setOcupaciones] = useState([]);
  const [girosIndependiente, setGirosIndependiente] = useState([]);
  const [sectoresLaborales, setSectoresLaborales] = useState([]);
  const [destinosCredito, setDestinosCredito] = useState([]);
  const [plazos, setPlazos] = useState([]);
  const [formasEnganche, setFormasEnganche] = useState([]);
  const [tiposInmueble, setTiposInmueble] = useState([]);
  const [afirmeCargado, setAfirmeCargado] = useState(false);

  useEffect(() => {
    cargarCatalogos();
  }, []);

// Cargar datos guardados de Afirme
useEffect(() => {
  const cargarDatosGuardados = async () => {
    if (!prospectoId) { setAfirmeCargado(true); return; }

    try {
      const response = await fetch(`/api/afirme/${prospectoId}`);
      if (response.ok) {
        const datos = await response.json();
        if (datos && datos.id) {
          // Solo cargar si hay datos guardados
          setFormData(prev => ({
            ...prev,
            afirme: datos
          }));
        }
      }
    } catch (error) {
      console.error('Error cargando datos Afirme:', error);
    } finally {
      setAfirmeCargado(true);
    }
  };

  cargarDatosGuardados();
}, [prospectoId]); // Solo ejecutar cuando cambie el prospectoId

// Estado Civil y Situación de Vivienda: se derivan del módulo Titular (no se
// capturan aquí). Se recalculan cada vez que cambian en Titular, y también una
// vez que terminó de cargar lo guardado de Afirme, para no quedar pisados por
// un valor anterior guardado en la base de datos.
useEffect(() => {
  if (!afirmeCargado) return;
  if (estadosCiviles.length === 0 || situacionesVivienda.length === 0) return;

  const nombreEC = nombreEstadoCivilAfirme(formData.titular.estado_civil, formData.titular.regimen_matrimonio);
  const ecId = idPorNombre(estadosCiviles, nombreEC);
  const svId = idPorNombre(situacionesVivienda, AFIRME_VIVE_EN_CASA_MAP[formData.titular.vive_en_casa]);

  setFormData(prev => {
    if (prev.afirme.estado_civil_id === ecId && prev.afirme.situacion_vivienda_id === svId) return prev;
    return { ...prev, afirme: { ...prev.afirme, estado_civil_id: ecId, situacion_vivienda_id: svId } };
  });
}, [afirmeCargado, estadosCiviles, situacionesVivienda, formData.titular.estado_civil, formData.titular.regimen_matrimonio, formData.titular.vive_en_casa]);

  const cargarCatalogos = async () => {
    try {
      const [ec, ge, sv, oc, gi, sl, dc, pl, fe, ti] = await Promise.all([
        fetch(`${API}/catalogos/afirme/estado_civil`).then(r => r.json()),
        fetch(`${API}/catalogos/afirme/escolaridad`).then(r => r.json()),
        fetch(`${API}/catalogos/afirme/situacion_vivienda`).then(r => r.json()),
        fetch(`${API}/catalogos/afirme/ocupacion`).then(r => r.json()),
        fetch(`${API}/catalogos/afirme/giro_independientes`).then(r => r.json()),
        fetch(`${API}/catalogos/afirme/sector_laboral`).then(r => r.json()),
        fetch(`${API}/catalogos/afirme/destino_credito`).then(r => r.json()),
        fetch(`${API}/catalogos/afirme/plazo_credito`).then(r => r.json()),
        fetch(`${API}/catalogos/afirme/forma_enganche`).then(r => r.json()),
        fetch(`${API}/catalogos/afirme/tipo_inmueble`).then(r => r.json())
      ]);
      
      setEstadosCiviles(ec);
      setGradosEstudios(ge);
      setSituacionesVivienda(sv);
      setOcupaciones(oc);
      setGirosIndependiente(gi);
      setSectoresLaborales(sl);
      setDestinosCredito(dc);
      setPlazos(pl);
      setFormasEnganche(fe);
      setTiposInmueble(ti);
    } catch (error) {
      console.error('Error cargando catálogos:', error);
    }
  };

  const updateField = (campo, valor) => {
    setFormData(prev => ({
      ...prev,
      afirme: {
        ...prev.afirme,
        [campo]: valor
      }
    }));

    // Calcular ingreso neto titular
    if (['titular_ingreso_mensual_bruto', 'titular_gasto_mensual', 'titular_pago_mensual_renta'].includes(campo)) {
      calcularIngresoNeto('titular');
    }
    
    // Calcular ingreso neto coacreditado
    if (['coac_ingreso_mensual_bruto', 'coac_gasto_mensual', 'coac_pago_mensual_renta'].includes(campo)) {
      calcularIngresoNeto('coac');
    }
  };

const handleSaveBankInfo = async () => {  // ✅ FUERA DE updateField
  if (!prospectoId) {
    alert('Primero debe guardar el prospecto antes de agregar información del banco');
    return;
  }
  if (creditoExcedeInmueble(formData.afirme.importe_credito_solicitado, formData.afirme.valor_vivienda)) { alert(MENSAJE_CREDITO_INMUEBLE); return; }
  
  try {
      const response = await fetch(`/api/afirme/${prospectoId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prospecto_id: prospectoId,
        ...formData.afirme
      })
    });
    
    const data = await response.json();
    
    if (data.success) {
      alert('✓ Información de Afirme guardada correctamente');
    } else {
      alert('Error: ' + data.error);
    }
  } catch (error) {
    console.error('Error:', error);
    alert('Error al guardar la información');
  }
};

  const calcularIngresoNeto = (tipo) => {
    setTimeout(() => {
      setFormData(prev => {
        const prefix = tipo === 'titular' ? 'titular_' : 'coac_';
        const ingreso = parseFloat(String(prev.afirme[`${prefix}ingreso_mensual_bruto`] || '').replace(/,/g, '')) || 0;
        const gasto = parseFloat(String(prev.afirme[`${prefix}gasto_mensual`] || '').replace(/,/g, '')) || 0;
        const renta = parseFloat(String(prev.afirme[`${prefix}pago_mensual_renta`] || '').replace(/,/g, '')) || 0;
        const neto = ingreso - gasto - renta;

        return {
          ...prev,
          afirme: {
            ...prev.afirme,
            [`${prefix}ingreso_neto`]: neto >= 0 ? Math.round(neto).toString() : '0'
          }
        };
      });
    }, 0);
  };

  const fmtMoney = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const num = Math.round(parseFloat(val));
    return isNaN(num) ? '' : num.toLocaleString('es-MX');
  };
  const parseMoney = (str) => String(str).replace(/[^0-9-]/g, '') || '';

  const SiNoRadio = ({ campo }) => {
    const val = formData.afirme[campo];
    return (
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <input type="radio" name={campo} checked={val === true} onChange={() => updateField(campo, true)} /> Sí
        </label>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <input type="radio" name={campo} checked={val === false} onChange={() => updateField(campo, false)} /> No
        </label>
      </div>
    );
  };

const st = {
  card: { backgroundColor: '#F0F0F0', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 20 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', color: '#1a3a6e', marginBottom: 16, borderBottom: '2px solid #f97316', paddingBottom: 8 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  inputNumber: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  select: { width: '100%', height: '42px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  grid3: { display: 'grid', gridTemplateColumns: gridCols('repeat(3, 1fr)'), gap: 16, marginBottom: 16 },
  grid4: { display: 'grid', gridTemplateColumns: gridCols('repeat(4, 1fr)'), gap: 16, marginBottom: 16 },
  grid5: { display: 'grid', gridTemplateColumns: gridCols('repeat(5, 1fr)'), gap: 16, marginBottom: 16 },
  gridTrabajo: { display: 'grid', gridTemplateColumns: gridCols('140px 1fr 1fr 1fr'), gap: 20, marginBottom: 16 },
  gridTrabajo2: { display: 'grid', gridTemplateColumns: gridCols('1fr 1fr 1fr'), gap: 20, marginBottom: 16 },
  gridIngresos: { display: 'grid', gridTemplateColumns: gridCols('repeat(4, 1fr)'), gap: 20, marginBottom: 16 },
  gridSeguro: { display: 'grid', gridTemplateColumns: gridCols('300px 200px 130px 130px'), gap: 25, marginBottom: 16 },
  gridCredito: { display: 'grid', gridTemplateColumns: gridCols('350px 250px 350px'), gap: 30, marginBottom: 16 }
};

  const destinoSeleccionado = destinosCredito.find(dc => String(dc.id) === String(formData.afirme.destino_credito_id));
  const esTerrenoMasConstruccion = !!(destinoSeleccionado && /terreno.*construcci/i.test(destinoSeleccionado.nombre));

  return (
    <div>
      {/* INFORMACIÓN GENERAL TITULAR */}
      <div style={st.card}>
        <h3 style={st.sectionTitle}>📋 Información General del Titular</h3>
        
        <div style={st.grid3}>
          <div style={st.field}>
            <label style={st.label}>¿Es Cliente Afirme?</label>
            <select value={formData.afirme.cliente_afirme ? 'true' : 'false'} onChange={e => updateField('cliente_afirme', e.target.value === 'true')} style={st.input}>
              <option value="">-- Seleccionar --</option>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </div>
          
          <div style={st.field}>
            <label style={st.label}>¿Es accionista o tiene crédito con Afirme?</label>
            <select value={formData.afirme.es_accionista_credito_afirme ? 'true' : 'false'} onChange={e => updateField('es_accionista_credito_afirme', e.target.value === 'true')} style={st.input}>
              <option value="">-- Seleccionar --</option>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </div>
          
          {formData.afirme.es_accionista_credito_afirme && (
            <div style={st.field}>
              <label style={st.label}>Nombre de la Empresa</label>
              <input value={formData.afirme.nombre_empresa_accionista} onChange={e => updateField('nombre_empresa_accionista', e.target.value)} style={st.input} />
            </div>
          )}
        </div>

        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Estado Civil</label>
            <input
              value={
                AFIRME_ESTADOS_CIVILES_OTRO_ESPECIFICAR.includes(formData.titular.estado_civil)
                  ? 'Otro (especificar)'
                  : estadosCiviles.find(ec => ec.id === formData.afirme.estado_civil_id)?.nombre || ''
              }
              readOnly
              style={{ ...st.input, backgroundColor: '#f3f4f6' }}
              title="Se captura en la pestaña Titular"
            />
          </div>
          
          <div style={st.field}>
            <label style={st.label}>Grado Máximo de Estudios *</label>
            <select value={formData.afirme.grado_estudios_id} onChange={e => updateField('grado_estudios_id', e.target.value)} style={st.input}>
              <option value="">-- Seleccionar --</option>
              {gradosEstudios.map(ge => <option key={ge.id} value={ge.id}>{ge.nombre}</option>)}
            </select>
          </div>
          
          <div style={st.field}>
            <label style={st.label}>Tipo Institución Estudios</label>
            <select value={formData.afirme.tipo_institucion_estudios} onChange={e => updateField('tipo_institucion_estudios', e.target.value)} style={st.input}>
              <option value="">-- Seleccionar --</option>
              <option value="publica">Pública</option>
              <option value="privada">Privada</option>
            </select>
          </div>
          
          <div style={st.field}>
            <label style={st.label}>Situación de Vivienda</label>
            <input
              value={situacionesVivienda.find(sv => sv.id === formData.afirme.situacion_vivienda_id)?.nombre || ''}
              readOnly
              style={{ ...st.input, backgroundColor: '#f3f4f6' }}
              title="Se captura en la pestaña Titular"
            />
          </div>
        </div>
      </div>

      {/* INFORMACIÓN TRABAJO TITULAR */}
      <div style={st.card}>
        <h3 style={st.sectionTitle}>💼 Información del Trabajo del Titular</h3>
        
        <div style={st.gridTrabajo}>
          <div style={st.field}>
            <label style={st.label}>Años en el Ramo</label>
            <input type="number" maxLength={2} value={formData.afirme.titular_anos_ramo} onChange={e => updateField('titular_anos_ramo', e.target.value)} style={st.inputNumber} />
          </div>
          
          <div style={st.field}>
            <label style={st.label}>Ocupación Actual *</label>
            <select value={formData.afirme.titular_ocupacion_actual_id} onChange={e => updateField('titular_ocupacion_actual_id', e.target.value)} style={st.input}>
              <option value="">-- Seleccionar --</option>
              {ocupaciones.map(oc => <option key={oc.id} value={oc.id}>{oc.nombre}</option>)}
            </select>
          </div>
          
          <div style={st.field}>
            <label style={st.label}>Giro de Independiente</label>
            <select value={formData.afirme.titular_giro_independiente_id} onChange={e => updateField('titular_giro_independiente_id', e.target.value)} style={st.input}>
              <option value="">-- Seleccionar --</option>
              {girosIndependiente.map(gi => <option key={gi.id} value={gi.id}>{gi.nombre}</option>)}
            </select>
          </div>
          
          <div style={st.field}>
            <label style={st.label}>Sector Laboral *</label>
            <select value={formData.afirme.titular_sector_laboral_id} onChange={e => updateField('titular_sector_laboral_id', e.target.value)} style={st.input}>
              <option value="">-- Seleccionar --</option>
              {sectoresLaborales.map(sl => <option key={sl.id} value={sl.id}>{sl.nombre}</option>)}
            </select>
          </div>
        </div>

        <div style={st.gridTrabajo2}>
          <div style={st.field}>
            <label style={st.label}>Actividad Específica en el Empleo</label>
            <input value={formData.afirme.titular_actividad_especifica} onChange={e => updateField('titular_actividad_especifica', e.target.value)} style={st.input} />
          </div>
          
          <div style={st.field}>
            <label style={st.label}>Nombre Jefe Inmediato</label>
            <input value={formData.afirme.titular_nombre_jefe_inmediato} onChange={e => updateField('titular_nombre_jefe_inmediato', e.target.value)} style={st.input} />
          </div>
            {!(parseInt(formData.titular.antiguedad_trabajo_anos, 10) >= 2) && (
            <div style={st.field}>
            <label style={st.label}>Nombre Jefe Anterior</label>
            <input value={formData.titular.nombre_jefe_anterior || ''} readOnly style={{ ...st.input, backgroundColor: '#f3f4f6' }} />
          </div>
            )}
        </div>

        <div style={st.gridIngresos}>
          <div style={st.field}>
            <label style={st.label}>Ingreso Mensual Bruto *</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 }}>$</span>
              <input
                type="text"
                value={fmtMoney(formData.afirme.titular_ingreso_mensual_bruto)}
                onChange={e => updateField('titular_ingreso_mensual_bruto', parseMoney(e.target.value))}
                style={{ ...st.inputNumber, paddingLeft: 24 }}
                placeholder="0"
              />
            </div>
          </div>
          <div style={st.field}>
            <label style={st.label}>Gasto Mensual</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 }}>$</span>
              <input
                type="text"
                value={fmtMoney(formData.afirme.titular_gasto_mensual)}
                onChange={e => updateField('titular_gasto_mensual', parseMoney(e.target.value))}
                style={{ ...st.inputNumber, paddingLeft: 24 }}
                placeholder="0"
              />
            </div>
          </div>

          <div style={st.field}>
            <label style={st.label}>Pago Mensual de Renta</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 }}>$</span>
              <input
                type="text"
                value={fmtMoney(formData.afirme.titular_pago_mensual_renta)}
                onChange={e => updateField('titular_pago_mensual_renta', parseMoney(e.target.value))}
                style={{ ...st.inputNumber, paddingLeft: 24 }}
                placeholder="0"
              />
            </div>
          </div>

          <div style={st.field}>
            <label style={st.label}>Ingreso Neto (Calculado)</label>
            <input
              type="text"
              value={`$${parseInt(formData.afirme.titular_ingreso_neto || 0).toLocaleString('es-MX')}`}
              readOnly
              style={{ ...st.input, backgroundColor: '#f3f4f6', fontWeight: 'bold' }}
            />
          </div>
        </div>
      </div>

      {/* INFORMACIÓN TRABAJO COACREDITADO */}
      {formData.coacreditado.nombres && formData.coacreditado.participa_ingresos && (
        <div style={st.card}>
          <h3 style={st.sectionTitle}>💼 Información del Trabajo del Coacreditado</h3>
          
          <div style={st.gridTrabajo}>
            <div style={st.field}>
              <label style={st.label}>Años en el Ramo</label>
              <input type="number" maxLength={2} value={formData.afirme.coac_anos_ramo} onChange={e => updateField('coac_anos_ramo', e.target.value)} style={st.inputNumber} />
            </div>
            
            <div style={st.field}>
              <label style={st.label}>Ocupación Actual</label>
              <select value={formData.afirme.coac_ocupacion_actual_id} onChange={e => updateField('coac_ocupacion_actual_id', e.target.value)} style={st.input}>
                <option value="">-- Seleccionar --</option>
                {ocupaciones.map(oc => <option key={oc.id} value={oc.id}>{oc.nombre}</option>)}
              </select>
            </div>
            
            <div style={st.field}>
              <label style={st.label}>Giro de Independiente</label>
              <select value={formData.afirme.coac_giro_independiente_id} onChange={e => updateField('coac_giro_independiente_id', e.target.value)} style={st.input}>
                <option value="">-- Seleccionar --</option>
                {girosIndependiente.map(gi => <option key={gi.id} value={gi.id}>{gi.nombre}</option>)}
              </select>
            </div>
            
            <div style={st.field}>
              <label style={st.label}>Sector Laboral</label>
              <select value={formData.afirme.coac_sector_laboral_id} onChange={e => updateField('coac_sector_laboral_id', e.target.value)} style={st.input}>
                <option value="">-- Seleccionar --</option>
                {sectoresLaborales.map(sl => <option key={sl.id} value={sl.id}>{sl.nombre}</option>)}
              </select>
            </div>
          </div>

          <div style={st.gridTrabajo2}>
            <div style={st.field}>
              <label style={st.label}>Actividad Específica en el Empleo</label>
              <input value={formData.afirme.coac_actividad_especifica} onChange={e => updateField('coac_actividad_especifica', e.target.value)} style={st.input} />
            </div>
            
            <div style={st.field}>
              <label style={st.label}>Nombre Jefe Inmediato</label>
              <input value={formData.afirme.coac_nombre_jefe_inmediato} onChange={e => updateField('coac_nombre_jefe_inmediato', e.target.value)} style={st.input} />
            </div>
            
            {!(parseInt(formData.coacreditado.antiguedad_trabajo_anos, 10) >= 2) && (
            <div style={st.field}>
              <label style={st.label}>Nombre Jefe Anterior</label>
              <input value={formData.coacreditado.nombre_jefe_anterior || ''} readOnly style={{ ...st.input, backgroundColor: '#f3f4f6' }} />
            </div>
            )}
          </div>

          <div style={st.gridIngresos}>
            <div style={st.field}>
              <label style={st.label}>Ingreso Mensual Bruto</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 }}>$</span>
                <input
                  type="text"
                  value={fmtMoney(formData.afirme.coac_ingreso_mensual_bruto)}
                  onChange={e => updateField('coac_ingreso_mensual_bruto', parseMoney(e.target.value))}
                  style={{ ...st.inputNumber, paddingLeft: 24 }}
                  placeholder="0"
                />
              </div>
            </div>

            <div style={st.field}>
              <label style={st.label}>Gasto Mensual</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 }}>$</span>
                <input
                  type="text"
                  value={fmtMoney(formData.afirme.coac_gasto_mensual)}
                  onChange={e => updateField('coac_gasto_mensual', parseMoney(e.target.value))}
                  style={{ ...st.inputNumber, paddingLeft: 24 }}
                  placeholder="0"
                />
              </div>
            </div>

            <div style={st.field}>
              <label style={st.label}>Otros</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 }}>$</span>
                <input
                  type="text"
                  value={fmtMoney(formData.afirme.coac_pago_mensual_renta)}
                  onChange={e => updateField('coac_pago_mensual_renta', parseMoney(e.target.value))}
                  style={{ ...st.inputNumber, paddingLeft: 24 }}
                  placeholder="0"
                />
              </div>
            </div>

            <div style={st.field}>
              <label style={st.label}>Ingreso Neto (Calculado)</label>
              <input
                type="text"
                value={`$${parseInt(formData.afirme.coac_ingreso_neto || 0).toLocaleString('es-MX')}`}
                readOnly
                style={{ ...st.input, backgroundColor: '#f3f4f6', fontWeight: 'bold' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* SEGURO TITULAR */}
      <div style={st.card}>
        <h3 style={st.sectionTitle}>🏥 Seguro Colectivo de Vida del Titular</h3>
       
        <div style={st.gridSeguro}>
          <div style={st.field}>
            <label style={st.label}>Nombre</label>
            <input value={`${formData.titular.nombres} ${formData.titular.apellido_paterno} ${formData.titular.apellido_materno}`} readOnly style={{ ...st.input, backgroundColor: '#f3f4f6' }} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Fecha Nac.</label>
            <input value={formData.titular.fecha_nacimiento} readOnly style={{ ...st.input, backgroundColor: '#f3f4f6' }} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Estatura (cm)</label>
            <input type="number" readOnly style={{ ...st.inputNumber, backgroundColor: '#f3f4f6' }} value={formData.titular.estatura_cms ?? ''} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Peso (kg)</label>
            <input type="number" readOnly style={{ ...st.inputNumber, backgroundColor: '#f3f4f6' }} value={formData.titular.peso_kg ?? ''} />
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', gap: 16 }}>
            <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>¿Ha presentado algún padecimiento relacionado con el corazón, sistema circulatorio, vías respiratorias, aparato digestivo, reproductor o vías urinarias?</span>
            <SiNoRadio campo="titular_padecimiento_corazon" />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', gap: 16 }}>
            <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>¿Presenta alguna enfermedad como Diabetes, Cáncer, Tumores o SIDA?</span>
            <SiNoRadio campo="titular_padecimiento_diabetes" />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', gap: 16 }}>
            <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>¿Está bajo algún tipo de tratamiento médico o tiene programada alguna intervención quirúrgica?</span>
            <SiNoRadio campo="titular_tratamiento_medico" />
          </div>
        </div>

        {(formData.afirme.titular_padecimiento_corazon || formData.afirme.titular_padecimiento_diabetes || formData.afirme.titular_tratamiento_medico) && (
          <div style={{ marginTop: 20, padding: 16, backgroundColor: '#fef3c7', borderRadius: 8 }}>
            <h4 style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 12 }}>Detalle de Padecimientos - Renglón 1</h4>
            <div style={st.grid4}>
              <div style={st.field}>
                <label style={st.label}>Enfermedad/Lesión/Tratamiento</label>
                <input value={formData.afirme.titular_seguro_enfermedad_1} onChange={e => updateField('titular_seguro_enfermedad_1', e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Duración</label>
                <input value={formData.afirme.titular_seguro_duracion_1} onChange={e => updateField('titular_seguro_duracion_1', e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Fecha</label>
                <input type="date" value={formData.afirme.titular_seguro_fecha_1} onChange={e => updateField('titular_seguro_fecha_1', e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Condición Actual</label>
                <input value={formData.afirme.titular_seguro_condicion_1} onChange={e => updateField('titular_seguro_condicion_1', e.target.value)} style={st.input} />
              </div>
            </div>

            <h4 style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 12, marginTop: 16 }}>Detalle de Padecimientos - Renglón 2</h4>
            <div style={st.grid4}>
              <div style={st.field}>
                <label style={st.label}>Enfermedad/Lesión/Tratamiento</label>
                <input value={formData.afirme.titular_seguro_enfermedad_2} onChange={e => updateField('titular_seguro_enfermedad_2', e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Duración</label>
                <input value={formData.afirme.titular_seguro_duracion_2} onChange={e => updateField('titular_seguro_duracion_2', e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Fecha</label>
                <input type="date" value={formData.afirme.titular_seguro_fecha_2} onChange={e => updateField('titular_seguro_fecha_2', e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Condición Actual</label>
                <input value={formData.afirme.titular_seguro_condicion_2} onChange={e => updateField('titular_seguro_condicion_2', e.target.value)} style={st.input} />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* SEGURO COACREDITADO */}
      {formData.coacreditado.nombres && (
        <div style={st.card}>
          <h3 style={st.sectionTitle}>🏥 Seguro Colectivo de Vida del Coacreditado</h3>
          
{/* SEGURO COACREDITADO - Todo en una fila */}
<div style={{display: 'grid', gridTemplateColumns: gridCols('320px 220px 160px 140px 140px'), gap: '30px', marginBottom: '15px'}}>
  <div style={st.field}>
    <label style={st.label}>Nombre</label>
    <input type="text" readOnly style={{ ...st.input, backgroundColor: '#f3f4f6' }} value={`${formData.coacreditado?.nombres || ''} ${formData.coacreditado?.apellido_paterno || ''} ${formData.coacreditado?.apellido_materno || ''}`.trim()} />
  </div>
  <div style={st.field}>
    <label style={st.label}>Fecha Nac.</label>
    <input type="date" readOnly style={{ ...st.input, backgroundColor: '#f3f4f6' }} value={formData.coacreditado?.fecha_nacimiento || ''} />
  </div>
  <div style={st.field}>
    <label style={st.label}>Elegible</label>
    <select style={st.select} value={formData.afirme.coacreditado_seguro_elegible} onChange={e => updateField('coacreditado_seguro_elegible', e.target.value)}>
      <option value="">-- Seleccionar --</option>
      <option value="Si">Si</option>
      <option value="No">No</option>
    </select>
  </div>
  <div style={st.field}>
    <label style={st.label}>Estatura (cm)</label>
    <input type="number" readOnly style={{ ...st.inputNumber, backgroundColor: '#f3f4f6' }} value={formData.coacreditado?.estatura_cms ?? ''} />
  </div>
  <div style={st.field}>
    <label style={st.label}>Peso (kg)</label>
    <input type="number" readOnly style={{ ...st.inputNumber, backgroundColor: '#f3f4f6' }} value={formData.coacreditado?.peso_kg ?? ''} />
  </div>
</div>

          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', gap: 16 }}>
              <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>¿Ha presentado algún padecimiento relacionado con el corazón, sistema circulatorio, vías respiratorias, aparato digestivo, reproductor o vías urinarias?</span>
              <SiNoRadio campo="coac_padecimiento_corazon" />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', gap: 16 }}>
              <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>¿Presenta alguna enfermedad como Diabetes, Cáncer, Tumores o SIDA?</span>
              <SiNoRadio campo="coac_padecimiento_diabetes" />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', gap: 16 }}>
              <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>¿Está bajo algún tipo de tratamiento médico o tiene programada alguna intervención quirúrgica?</span>
              <SiNoRadio campo="coac_tratamiento_medico" />
            </div>
          </div>

          {(formData.afirme.coac_padecimiento_corazon || formData.afirme.coac_padecimiento_diabetes || formData.afirme.coac_tratamiento_medico) && (
            <div style={{ marginTop: 20, padding: 16, backgroundColor: '#fef3c7', borderRadius: 8 }}>
              <h4 style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 12 }}>Detalle de Padecimientos - Renglón 1</h4>
              <div style={st.grid4}>
                <div style={st.field}>
                  <label style={st.label}>Enfermedad/Lesión/Tratamiento</label>
                  <input value={formData.afirme.coac_seguro_enfermedad_1} onChange={e => updateField('coac_seguro_enfermedad_1', e.target.value)} style={st.input} />
                </div>
                <div style={st.field}>
                  <label style={st.label}>Duración</label>
                  <input value={formData.afirme.coac_seguro_duracion_1} onChange={e => updateField('coac_seguro_duracion_1', e.target.value)} style={st.input} />
                </div>
                <div style={st.field}>
                  <label style={st.label}>Fecha</label>
                  <input type="date" value={formData.afirme.coac_seguro_fecha_1} onChange={e => updateField('coac_seguro_fecha_1', e.target.value)} style={st.input} />
                </div>
                <div style={st.field}>
                  <label style={st.label}>Condición Actual</label>
                  <input value={formData.afirme.coac_seguro_condicion_1} onChange={e => updateField('coac_seguro_condicion_1', e.target.value)} style={st.input} />
                </div>
              </div>

              <h4 style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 12, marginTop: 16 }}>Detalle de Padecimientos - Renglón 2</h4>
              <div style={st.grid4}>
                <div style={st.field}>
                  <label style={st.label}>Enfermedad/Lesión/Tratamiento</label>
                  <input value={formData.afirme.coac_seguro_enfermedad_2} onChange={e => updateField('coac_seguro_enfermedad_2', e.target.value)} style={st.input} />
                </div>
                <div style={st.field}>
                  <label style={st.label}>Duración</label>
                  <input value={formData.afirme.coac_seguro_duracion_2} onChange={e => updateField('coac_seguro_duracion_2', e.target.value)} style={st.input} />
                </div>
                <div style={st.field}>
                  <label style={st.label}>Fecha</label>
                  <input type="date" value={formData.afirme.coac_seguro_fecha_2} onChange={e => updateField('coac_seguro_fecha_2', e.target.value)} style={st.input} />
                </div>
                <div style={st.field}>
                  <label style={st.label}>Condición Actual</label>
                  <input value={formData.afirme.coac_seguro_condicion_2} onChange={e => updateField('coac_seguro_condicion_2', e.target.value)} style={st.input} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* INFORMACIÓN DEL CRÉDITO */}
      <div style={st.card}>
        <h3 style={st.sectionTitle}>💰 Información del Crédito</h3>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div style={{ ...st.field, width: isMobile ? '100%' : 515 }}>
            <label style={st.label}>Destino del Crédito *</label>
            <select value={formData.afirme.destino_credito_id} onChange={e => updateField('destino_credito_id', e.target.value)} style={{ ...st.input, width: '100%' }}>
              <option value="">-- Seleccionar --</option>
              {destinosCredito.map(dc => <option key={dc.id} value={dc.id}>{dc.nombre}</option>)}
            </select>
          </div>

          <div style={{ ...st.field, width: isMobile ? '100%' : 326 }}>
            <label style={st.label}>Oferta Vinculante</label>
            <select value={formData.afirme.oferta_vinculante ? 'true' : 'false'} onChange={e => updateField('oferta_vinculante', e.target.value === 'true')} style={{ ...st.input, width: '100%' }}>
              <option value="">-- Seleccionar --</option>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>

        {esTerrenoMasConstruccion && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, padding: 12, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <div style={{ ...st.field, width: isMobile ? '100%' : 250 }}>
              <label style={st.label}>El terreno tiene hipoteca</label>
              <select value={formData.afirme.terreno_tiene_hipoteca ? 'true' : 'false'} onChange={e => updateField('terreno_tiene_hipoteca', e.target.value === 'true')} style={{ ...st.input, width: '100%' }}>
                <option value="">-- Seleccionar --</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>

            {formData.afirme.terreno_tiene_hipoteca && (
              <div style={{ ...st.field, width: isMobile ? '100%' : 215 }}>
                <label style={st.label}>Monto Deudor</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 }}>$</span>
                  <input
                    type="text"
                    value={fmtMoney(formData.afirme.terreno_monto_deudor)}
                    onChange={e => updateField('terreno_monto_deudor', parseMoney(e.target.value))}
                    style={{ ...st.inputNumber, paddingLeft: 24, width: '100%' }}
                    placeholder="0"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div style={{ ...st.field, width: isMobile ? '100%' : 215 }}>
            <label style={st.label}>Importe Crédito Solicitado *</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 }}>$</span>
              <input
                type="text"
                value={fmtMoney(formData.afirme.importe_credito_solicitado)}
                onChange={e => updateField('importe_credito_solicitado', parseMoney(e.target.value))}
                style={{ ...st.inputNumber, paddingLeft: 24, width: '100%' }}
                placeholder="0"
              />
            </div>
          </div>

          <div style={{ ...st.field, width: isMobile ? '100%' : 212 }}>
            <label style={st.label}>Valor de la Vivienda *</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 }}>$</span>
              <input
                type="text"
                value={fmtMoney(formData.afirme.valor_vivienda)}
                onChange={e => updateField('valor_vivienda', parseMoney(e.target.value))}
                style={{ ...st.inputNumber, paddingLeft: 24, width: '100%' }}
                placeholder="0"
              />
            </div>
          </div>

          <div style={{ ...st.field, width: isMobile ? '100%' : 212 }}>
            <label style={st.label}>Tasa (%)</label>
            <input
              type="number"
              step="0.01"
              value={formData.afirme.tasa}
              onChange={e => updateField('tasa', e.target.value)}
              style={{ ...st.inputNumber, width: '100%' }}
              placeholder="0.00%"
            />
          </div>

          <div style={{ ...st.field, width: isMobile ? '100%' : 212 }}>
            <label style={st.label}>Plazo en Años *</label>
            <select value={formData.afirme.plazo_anos_id} onChange={e => updateField('plazo_anos_id', e.target.value)} style={{ ...st.input, width: '100%' }}>
              <option value="">-- Seleccionar --</option>
              {plazos.map(pl => <option key={pl.id} value={pl.id}>{pl.nombre}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div style={{ ...st.field, width: isMobile ? '100%' : 204 }}>
            <label style={st.label}>Principal Forma de Enganche</label>
            <select value={formData.afirme.principal_forma_enganche_id} onChange={e => updateField('principal_forma_enganche_id', e.target.value)} style={{ ...st.input, width: '100%' }}>
              <option value="">-- Seleccionar --</option>
              {formasEnganche.map(fe => <option key={fe.id} value={fe.id}>{fe.nombre}</option>)}
            </select>
          </div>

          <div style={{ ...st.field, width: isMobile ? '100%' : 157 }}>
            <label style={st.label}>Tipo de Inmueble *</label>
            <select value={formData.afirme.tipo_inmueble_id} onChange={e => updateField('tipo_inmueble_id', e.target.value)} style={{ ...st.input, width: '100%' }}>
              <option value="">-- Seleccionar --</option>
              {tiposInmueble.map(ti => <option key={ti.id} value={ti.id}>{ti.nombre}</option>)}
            </select>
          </div>

          <div style={{ ...st.field, width: isMobile ? '100%' : 157 }}>
            <label style={st.label}>¿Vivienda Principal?</label>
            <select value={formData.afirme.vivienda_principal ? 'true' : 'false'} onChange={e => updateField('vivienda_principal', e.target.value === 'true')} style={{ ...st.input, width: '100%' }}>
              <option value="">-- Seleccionar --</option>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </div>

          <div style={{ ...st.field, width: isMobile ? '100%' : 393 }}>
            <label style={st.label}>Municipio de la Vivienda</label>
            <input value={formData.afirme.municipio_vivienda} onChange={e => updateField('municipio_vivienda', e.target.value)} style={{ ...st.input, width: '100%' }} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
          <button
            type="button"
            onClick={handleSaveBankInfo}
            style={{
              padding: '12px 30px',
              backgroundColor: '#1a3a6e',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            💾 Guardar Información de Afirme
          </button>
        </div>
      </div>
    </div>
  );
};

export default BancoAfirme;
